import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslate, TranslateScope } from '@pegasus/translation';
import { ContactForm } from '@pegasus/contact-form';
import { BLOCKS, INLINES } from '@contentful/rich-text-types';
import { Options } from '@contentful/rich-text-react-renderer';
import { useTranslationContext } from '@pegasus/translation';
import { getCSRFToken } from 'src/client/helpers';

import {
    addErrorFlashMessage,
    addSuccessFlashMessage,
} from 'src/client/components/shared/flash-messages';
import { FormWrapper, Table } from './styles';
import { getIsGuest } from '../Header/customer/redux/customer';
import { useCustomer } from '../../hooks/useCustomer';

interface ContentTableProps {
    tableBody: string;
}

export const ContentTable: React.FC<ContentTableProps> = ({ tableBody }) => {
    const translate = useTranslate();
    // Can find "${key}" or "${key.nestedKey}"
    const translationPlaceholder = /\${(\w*\.*)*}/g;
    // Can find "key" or "key.nestedKey"
    const translationKey = /\$*\{*\}*/g;

    if (!tableBody) {
        return null;
    }

    return (
        <Table
            dangerouslySetInnerHTML={{
                __html: tableBody.replace(translationPlaceholder, (match: string) =>
                    translate(match.replace(translationKey, '')),
                ),
            }}
        />
    );
};

export function getRenderOptions(pageClassName: string): Options {
    return {
        renderNode: {
            [BLOCKS.PARAGRAPH]: (node, children) => {
                if (pageClassName) {
                    return <p className={pageClassName}>{children}</p>;
                }
                return <p>{children}</p>;
            },
            [INLINES.EMBEDDED_ENTRY]: ({ data }) => {
                const { file } = data?.target?.fields.media.fields || {};
                return (
                    <video width="100%" height="100%" controls>
                        <source src={file.url} type={file.contentType} />
                        <track kind="captions" />
                    </video>
                );
            },
            [BLOCKS.EMBEDDED_ASSET]: ({ data }) => {
                const { title, width, height, file } = data?.target?.fields || {};
                return (
                    <img
                        src={`https://${file?.url}`}
                        height={height?.details?.image?.height}
                        width={width?.details?.image?.width}
                        alt={title?.title}
                    />
                );
            },
            [BLOCKS.EMBEDDED_ENTRY]: node => {
                const { type, text } = node.data?.target?.fields || {};
                const csrfToken = getCSRFToken();
                const dispatch = useDispatch();
                const isGuest = useSelector(getIsGuest);
                const { customer } = useCustomer();

                const defaultData =
                    !isGuest && customer
                        ? {
                              name: `${customer.firstName} ${customer.lastName}`,
                              phone: customer.phone || '',
                              email: customer.email,
                          }
                        : {
                              name: '',
                              phone: '',
                              email: '',
                          };
                switch (type) {
                    case 'contactForm': {
                        const { translations } = useTranslationContext();
                        return (
                            <FormWrapper>
                                <ContactForm
                                    defaultData={defaultData}
                                    csrfToken={csrfToken}
                                    url="/api/contact-form/contacts"
                                    translations={translations}
                                    submitSuccess={(successFlashMessage: string) =>
                                        dispatch(addSuccessFlashMessage(successFlashMessage))
                                    }
                                    submitError={(error: string) =>
                                        dispatch(addErrorFlashMessage(error))
                                    }
                                />
                            </FormWrapper>
                        );
                    }
                    case 'table': {
                        return (
                            <TranslateScope>
                                <ContentTable tableBody={text.content?.[0]?.content?.[0]?.value} />
                            </TranslateScope>
                        );
                    }
                    default: {
                        return null;
                    }
                }
            },
        },
    };
}
